import React, { useCallback } from 'react';
import { GlassPanel as ReactGlassPanel, type GlassPanelProps as ReactGlassPanelProps } from 'react-glasskit';

interface GlassPanelProps extends ReactGlassPanelProps<'div'> {
    ref?: React.Ref<HTMLDivElement>;
    /** Called when this panel receives focus (click or keyboard). */
    onPanelFocus?: () => void;
    /** Accessible label for the focusable panel region. Prefer aria-label for new code. */
    ariaLabel?: string;
}

/**
 * App compatibility wrapper around react-glasskit's GlassPanel.
 * Keeps the repo's named, focusable panel contract while standardizing material styling.
 */
export const GlassPanel: React.FC<GlassPanelProps> = ({
    ref,
    focused = true,
    inactive,
    onPanelFocus,
    ariaLabel,
    onFocusCapture,
    role,
    tabIndex,
    'aria-label': ariaLabelAttribute,
    ...props
}) => {
    const handleFocusCapture = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        onPanelFocus?.();
        onFocusCapture?.(event);
    }, [onFocusCapture, onPanelFocus]);

    return (
        <ReactGlassPanel
            ref={ref}
            focused={focused}
            inactive={inactive ?? !focused}
            onFocusCapture={handleFocusCapture}
            role={role ?? 'region'}
            tabIndex={tabIndex ?? 0}
            aria-label={ariaLabel ?? ariaLabelAttribute}
            {...props}
        />
    );
};

GlassPanel.displayName = 'GlassPanel';
